/**
 * One dashboard or report shared with a client in their portal: loads it,
 * then shows it read-only. The client may change the period.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import type { Catalogue, Page } from '../reports/reports-api.ts';
import { portalApi, type Branding, type SharedDocument } from './portal-api.ts';
import { ReadOnlyReport } from './ReadOnlyReport.tsx';
import type { QueryPage } from './use-read-only-page.ts';

export function PortalViewer(props: {
  org: string;
  clientId: string;
  clientName: string;
  branding: Branding;
  catalogue: Catalogue;
  documentId: string;
  onBack: () => void;
}): ReactNode {
  const { org, clientId, documentId } = props;
  const [loaded, setLoaded] = useState<{ document: SharedDocument; pages: Page[] } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    portalApi
      .document(org, clientId, documentId)
      .then(setLoaded)
      .catch(() => setProblem('This report is no longer shared with you.'));
  }, [org, clientId, documentId]);

  const query = useCallback<QueryPage>(
    (input) => portalApi.query(org, clientId, documentId, input),
    [org, clientId, documentId],
  );

  if (problem)
    return (
      <div className="pt-empty" role="alert">
        {problem}{' '}
        <button type="button" className="btn small secondary" onClick={props.onBack}>
          Back
        </button>
      </div>
    );
  if (!loaded) return <div className="pt-empty">Loading…</div>;

  return (
    <ReadOnlyReport
      document={{ ...loaded.document, key: loaded.document.id }}
      pages={loaded.pages}
      clientName={props.clientName}
      branding={props.branding}
      catalogue={props.catalogue}
      query={query}
      canChangeRange
      leading={
        <button
          type="button"
          className="rb-icon"
          aria-label="Back to all reports"
          onClick={props.onBack}
        >
          ←
        </button>
      }
    />
  );
}
